import Image from "next/image";
import Link from "next/link";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { HomeRevealStripItem, HomeSectionTitle } from "@/components/HomeMotionPrimitivesStatic";
import {
  getSubCategoryImageSrc,
  sanitizeSubCategoryImageSrc,
} from "@/lib/subCategoryImage";
import { resolveLinkPrefetch } from "@/lib/usePrefetchRoutes";

/** Shown when API has no category rows yet (matches legacy mobile quick links). */
export const POPULAR_CATEGORIES_FALLBACK = [
  { Category: { slug: "fashion" }, SubCategory: { slug: "for-women", name: "Fashion" } },
  {
    Category: { slug: "jewelry-and-accessories" },
    SubCategory: { slug: "necklaces-and-pendants", name: "Jewelry & Accessories" },
  },
  {
    Category: { slug: "home-and-living" },
    SubCategory: { slug: "handcrafted-decor", name: "Home & Living" },
  },
  { Category: { slug: "toys-and-games" }, SubCategory: { slug: "toys", name: "Toys & Games" } },
  {
    Category: { slug: "art-and-collectibles" },
    SubCategory: { slug: "digital-art", name: "Art & Collectibles" },
  },
  {
    Category: { slug: "stationery-and-paper-goods" },
    SubCategory: { slug: "notebooks-and-journals", name: "Stationery & Paper" },
  },
  {
    Category: { slug: "personal-care-and-bath-products" },
    SubCategory: { slug: "handmade-soap", name: "Personal Care" },
  },
];

/** Match discounted / new-arrival carousel dot math. */
function computeCarouselPages(scrollWidth, clientWidth) {
  const maxScroll = Math.max(0, scrollWidth - clientWidth);
  if (maxScroll <= 0) return 1;
  const ceilPages = Math.ceil(scrollWidth / clientWidth);
  const floorPages = Math.floor(maxScroll / clientWidth) + 1;
  return Math.max(2, Math.min(ceilPages, floorPages));
}

const FeaturedCategoryLink = memo(function FeaturedCategoryLink({ category }) {
  const categoryId = category?.Category?.slug;
  const subCategoryId = category?.SubCategory?.slug;
  const title = category?.SubCategory?.name;
  const href =
    categoryId && subCategoryId
      ? `/category/${categoryId}/${subCategoryId}`
      : categoryId
        ? `/category/${categoryId}`
        : "/category";
  const imagePath = getSubCategoryImageSrc(subCategoryId);
  const [imgSrc, setImgSrc] = useState(imagePath);

  useEffect(() => {
    setImgSrc(imagePath);
  }, [imagePath]);

  return (
    <Link
      href={href}
      prefetch={resolveLinkPrefetch(href)}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      className="group flex w-[128px] flex-shrink-0 flex-col items-center touch-manipulation select-none sm:w-[140px] md:w-[172px] lg:w-[188px] xl:w-[198px]"
    >
        <div className="relative mb-3 flex h-[112px] w-[112px] items-center justify-center overflow-hidden rounded-full bg-gray-100 shadow-sm ring-1 ring-gray-200/90 transition-[box-shadow,ring-color] duration-200 sm:h-[124px] sm:w-[124px] md:mb-3.5 md:h-[156px] md:w-[156px] lg:h-[168px] lg:w-[168px] xl:h-[176px] xl:w-[176px] group-hover:shadow-md group-hover:ring-[#C9A227]/45">
          <Image
            src={sanitizeSubCategoryImageSrc(imgSrc)}
            alt={title || "Category"}
            width={360}
            height={360}
            draggable={false}
            className="h-full w-full object-cover [-webkit-user-drag:none] pointer-events-none"
            sizes="(max-width: 640px) 124px, (max-width: 1024px) 168px, 176px"
            loading="lazy"
            onError={() => setImgSrc("/placeholder.jpg")}
          />
        </div>
        <h3 className="line-clamp-2 min-h-[2.5rem] px-1 text-center text-xs font-bold leading-tight text-gray-800 sm:text-sm md:min-h-[2.75rem] md:text-[15px] md:leading-snug lg:text-base">
          {title}
        </h3>
      </Link>
  );
});

FeaturedCategoryLink.displayName = "FeaturedCategoryLink";

export default function PopularCategoriesSection({
  categories,
  title = "Shop Our Most Popular Categories",
}) {
  const scrollerRef = useRef(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  const items = useMemo(() => {
    if (categories && categories.length > 0) return categories;
    return POPULAR_CATEGORIES_FALLBACK;
  }, [categories]);

  const syncScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    if (clientWidth <= 0) return;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    if (maxScroll <= 0) {
      setPageCount(1);
      setPageIndex(0);
      return;
    }
    const pages = computeCarouselPages(scrollWidth, clientWidth);
    setPageCount(pages);
    const ratio = maxScroll > 0 ? scrollLeft / maxScroll : 0;
    const idx = Math.round(ratio * (pages - 1));
    setPageIndex(Math.min(pages - 1, Math.max(0, idx)));
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    let cleaned = false;
    let attached = false;
    let rafScroll = 0;
    let roRaf = 0;
    let rafInit = 0;
    let ro = null;

    const runSync = () => {
      if (!cleaned) syncScrollState();
    };

    const onScroll = () => {
      cancelAnimationFrame(rafScroll);
      rafScroll = requestAnimationFrame(() => {
        if (!cleaned) syncScrollState();
      });
    };

    const scheduleSync = () => {
      cancelAnimationFrame(roRaf);
      roRaf = requestAnimationFrame(() => {
        roRaf = 0;
        runSync();
      });
    };

    const detachListeners = () => {
      if (!attached) return;
      attached = false;
      cancelAnimationFrame(rafInit);
      cancelAnimationFrame(rafScroll);
      cancelAnimationFrame(roRaf);
      el.removeEventListener("scroll", onScroll);
      ro?.disconnect();
      ro = null;
      window.removeEventListener("resize", scheduleSync);
    };

    const attachListeners = () => {
      if (cleaned || attached) return;
      attached = true;
      rafInit = requestAnimationFrame(() => {
        rafInit = requestAnimationFrame(() => {
          rafInit = 0;
          if (!cleaned) runSync();
        });
      });
      el.addEventListener("scroll", onScroll, { passive: true });
      ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleSync) : null;
      ro?.observe(el);
      window.addEventListener("resize", scheduleSync);
    };

    if (typeof IntersectionObserver === "undefined") {
      attachListeners();
      return () => {
        cleaned = true;
        detachListeners();
      };
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        io.disconnect();
        attachListeners();
      },
      { root: null, rootMargin: "220px 0px", threshold: 0.01 }
    );
    io.observe(el);

    return () => {
      cleaned = true;
      io.disconnect();
      detachListeners();
    };
  }, [items.length, syncScrollState]);

  const goToPage = useCallback((i) => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollWidth, clientWidth } = el;
    if (clientWidth <= 0) return;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    if (maxScroll <= 0) return;
    const pages = computeCarouselPages(scrollWidth, clientWidth);
    const target = pages <= 1 ? 0 : (maxScroll * i) / (pages - 1);
    el.scrollTo({ left: target, behavior: "smooth" });
  }, []);

  return (
    <section id="category-section" className="overflow-hidden bg-white py-8 md:py-10">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-6 sm:mb-8">
          <HomeSectionTitle
            sharpText
            className={`noto-font text-2xl text-blue-950 md:text-3xl xl:text-4xl xl:leading-[52px] xl:tracking-[-0.96px] tracking-[-0.72px] md:tracking-[-0.72px]`}
          >
            {title}
          </HomeSectionTitle>
        </div>

        <div
          ref={scrollerRef}
          className="-mx-4 overflow-x-auto overscroll-x-contain px-4 pb-1 pt-1 scrollbar-hidden snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [touch-action:pan-x_pan-y] [scroll-padding-inline:1rem] max-md:pl-[max(1rem,env(safe-area-inset-left,0px))] max-md:pr-[max(1rem,env(safe-area-inset-right,0px))] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:overflow-visible lg:px-0 lg:[scroll-padding-inline:0]"
        >
          <div className="flex w-max flex-nowrap gap-5 md:gap-5 lg:w-full lg:justify-between lg:gap-4 xl:gap-6">
            {items.map((category, index) => (
              <HomeRevealStripItem
                key={`${category?.SubCategory?.slug || index}`}
                index={index}
                className="snap-start flex-shrink-0"
              >
                <FeaturedCategoryLink category={category} />
              </HomeRevealStripItem>
            ))}
          </div>
        </div>

        <div className="mt-6 min-h-2 md:mt-8">
          {pageCount > 1 ? (
            <div
            className="flex max-w-full flex-nowrap justify-center gap-0 overflow-x-auto overflow-y-visible py-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label="Category pages"
          >
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === pageIndex}
                aria-label={`Categories page ${i + 1} of ${pageCount}`}
                onClick={() => goToPage(i)}
                className="flex h-8 min-h-[32px] w-fit min-w-0 shrink-0 items-center justify-center px-0.5 rounded-full"
              >
                <span
                  className={
                    i === pageIndex
                      ? "h-2 w-8 rounded-full bg-primary transition-all duration-300"
                      : "h-2 w-2 rounded-full bg-gray-300 transition-all duration-300 hover:bg-gray-400"
                  }
                  aria-hidden
                />
              </button>
            ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
